import React from 'react';
import { ProjectionFiltersState } from './ProjectionsFilters';
import { ProjectionRow, StatusFilter, synthese } from './projectionsModel';

export type SortDirection = 'asc' | 'desc';
export type SortKey = keyof ProjectionRow;

export interface SortConfig {
    key: SortKey;
    direction: SortDirection;
}

export const formatCurrency = (val: number | null | undefined): string => {
    if (val === undefined || val === null) return '—';
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(val);
};

export const formatBillions = (val: number | null | undefined): string => {
    if (val === undefined || val === null) return '—';
    if (Math.abs(val) >= 1e9) return (val / 1e9).toLocaleString('fr-FR', { maximumFractionDigits: 1 }) + ' Md';
    if (Math.abs(val) >= 1e6) return (val / 1e6).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' M';
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(val);
};

export const formatPercent = (val: number | null | undefined): React.ReactNode => {
    if (val === undefined || val === null) return <span className="percent-badge">—</span>;
    const type = val > 0 ? 'up' : val < 0 ? 'down' : 'flat';
    return (
        <span className={`percent-badge percent-badge--${type}`}>
            {val > 0 ? '+' : ''}{(val * 100).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
        </span>
    );
};

export const getMethodBadge = (method: string | undefined | null): { label: string; cls: string } | null => {
    if (!method || method === 'none' || method.startsWith('no_')) return null;
    const m = method.toLowerCase();
    if (m.includes('annual'))   return { label: 'Annuel',    cls: 'method-badge--blue' };
    if (m.includes('s1_s2'))    return { label: 'S1+S2',     cls: 'method-badge--teal' };
    if (m.includes('9m_q4'))    return { label: '9M+Q4',     cls: 'method-badge--teal' };
    if (m.includes('s1_q3_q4')) return { label: 'S1+Q3+Q4', cls: 'method-badge--teal' };
    if (m.includes('_9m'))      return { label: 'Base 9M',   cls: 'method-badge--indigo' };
    if (m.includes('_s1'))      return { label: 'Base S1',   cls: 'method-badge--violet' };
    if (m.includes('flat'))     return { label: 'Non projeté', cls: 'method-badge--gray' };
    return null;
};

const fmtPct = (v: number) => `${v > 0 ? '+' : ''}${(v * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;

export const getCellTooltip = (
    estMethod: string | null,
    actualMethod: string | null,
    estVsActual: number | null,
    estValue: number | null,
    actualValue: number | null,
    label: string,
    expectedMape: number | null,
): string => {
    if (actualValue != null) {
        const m = (actualMethod || estMethod || '').toLowerCase();
        const src = m.includes('annual') ? 'Rapport annuel'
            : m.includes('s1_s2') ? 'Agrégat S1 + S2'
            : m.includes('9m_q4') ? 'Agrégat 9M + Q4'
            : 'Publication disponible';
        let tooltip = `${label} — valeur publiée\nSource : ${src}\nMontant : ${formatBillions(actualValue)}`;
        if (estVsActual != null) {
            tooltip += `\n\nEstimation du modèle avant publication : ${formatBillions(estValue)}`;
            tooltip += `\nÉcart modèle / publié : ${fmtPct(estVsActual)}`;
        }
        return tooltip;
    }
    const m = (estMethod || '').toLowerCase();
    if (m.includes('flat') || !m || m === 'none') {
        return `${label} — non projeté\nAucune publication intermédiaire de l'exercice en base : la valeur affichée est celle de l'exercice précédent, recopiée.`;
    }
    const base = m.includes('_9m') ? '9 mois publiés' : m.includes('_s1') ? 'premier semestre publié' : 'données partielles';
    let tooltip = `${label} — estimation\nBase : ${base}\nMéthode : 70 % extrapolation simple + 30 % ancrage sur l'exercice précédent`;
    if (expectedMape != null) tooltip += `\nErreur attendue pour ce secteur : ±${Math.round(expectedMape * 100)} %`;
    if (m.includes('divergent')) tooltip += '\n⚠ Les deux méthodes divergent de plus de 25 % : risque de retour à la moyenne.';
    return tooltip;
};

export type FinancialProjectionsViewProps = {
    data: ProjectionRow[];
    loading: boolean;
    error: string;
    cacheDate: string | null;
    annee: number | null;
    annees: number[];
    onAnneeChange: (annee: number) => void;
    keyword: string;
    setKeyword: (v: string) => void;
    sortConfig: SortConfig;
    handleSort: (key: SortKey) => void;
    statusFilter: StatusFilter;
    setStatusFilter: (v: StatusFilter) => void;
    filters: ProjectionFiltersState;
    setFilters: (f: ProjectionFiltersState) => void;
    processedData: ProjectionRow[];
    summaryMetrics: ReturnType<typeof synthese> | null;
};

